import { Component, OnInit, Inject } from "@angular/core";
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
} from "@angular/material/dialog";
import { MediaObserver } from "@angular/flex-layout";
import { Router } from "@angular/router";
import * as moment from "moment";
import { forkJoin } from "rxjs";

import { SalemethodService } from "src/app/services/api/salemethod/salemethod.service";
import { PreventLossPanierComponent } from "../../prevent-loss-panier/prevent-loss-panier.component";
import { CheckAddressComponent } from "../../check-address/check-address.component";
import { ToolsService } from "../../../../services/tools/tools.service";
import { TableReservationService } from "src/app/services/api/table-reservation/table-reservation.service";
import { ClientOrdersService } from "src/app/services/apiclient/order/clientorders.service";
import { TranslateService } from "@ngx-translate/core";
import { CssService } from "../../../../services/css-service";
import { DialogsService } from "../../../../services/manage/dialogs/dialogs.service";
import { PanierService } from "../../../../services/manage/panier/panier.service";
import { environment } from "src/environments/environment";
import { LogoAndBannerService } from "src/app/services/fulle/logoAndBanner/logoAndBanner.service";
import { PointOfSaleDetails } from "src/app/services/api/fulle/fulle-pointOfSale/fulle-pointOfSale.service.interface";
import { SaleMethod } from "src/app/components/order/fulle-selection-SM/fulle-selection-SM.interface";

/**
 * Payload minimal que renvoie l'API des slots (horaires) côté commande.
 * On ne type que ce que ce composant consomme réellement.
 */
type ValidSlot = { hour: string; available: boolean };
type ValidSlotsResponse = {
  code?: number; // ex: -82 => fermé
  day_changed?: string; // "YYYY-MM-DD"
  valid_slots?: ValidSlot[];
};

/**
 * Données optionnelles injectées par MatDialog lors de l'ouverture.
 * - currSmId: permet d'auto-sélectionner un SM (mode de vente) à l'ouverture
 * - fromUrl: indique si le dialog s'ouvre suite à une navigation/URL (et pas depuis le header)
 */
type SalemethodDialogData = {
  currSmId?: number | string;
  fromUrl?: boolean;
} | null;

@Component({
  selector: "app-fulle-salemethod",
  templateUrl: "./fulle-salemethod.component.html",
  styleUrls: ["./fulle-salemethod.component.css"],
})
export class FulleSalemethodComponent implements OnInit {
  isProd = environment.production;  // Indique si l'app tourne en prod (peut servir à afficher une bannière DEV, etc.)
  devBannerHeightPx = 34;           // Hauteur de la bannière DEV (si affichée)
  getBanner = "";                   // String utilisée par l'UI (ex: url de bannière / variable)

  /**
   * Point de vente sélectionné (stocké dans le local/session storage).
   * TODO : Je le laisse en any icipour le moment mais il faudra le passer en slim v2.
   */
  currPoint: any = this.tools.getStor("point_of_sale");

  /**
   * Flag stocké en storage pour le moment Je garde boolean | any car le storage renvoie parfois string/number suivant l'historique.
   * Mais TODO : il faudra le typer correctement.
   */
  alcoolCheck: boolean | any = this.tools.getStor("alcool_check");
  saleMethods$: SaleMethod[] = [];  // Liste des modes de vente affichés dans le dialog
  fromUrl = false;   // Vrai si le dialog a été ouvert via URL (et non via bouton header)
  currSmId?: number; // ID du mode de vente à auto-sélectionner (si fourni par le parent)
  bookingSettings: any = null; // Config réservation table (si active)
  loader = true; // Loader UI

  constructor(
    private smApi: SalemethodService,
    private dialog: MatDialog,
    private tools: ToolsService,
    private media: MediaObserver,
    private router: Router,
    private trService: TableReservationService,
    private orderApi: ClientOrdersService,
    private logoAndBannerService: LogoAndBannerService,
    public dialogRef: MatDialogRef<FulleSalemethodComponent>,
    public cssService: CssService,
    public dialogsService: DialogsService,
    public panierService: PanierService,
    public translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) data: SalemethodDialogData
  ) {
    // Normalisation des données injectées (typage + conversions)
    if (data) {
      this.fromUrl = Boolean(data.fromUrl);
      // currSmId peut être string venant de params URL, on le convertit en number
      const parsed = Number(data.currSmId);
      this.currSmId = Number.isFinite(parsed) ? parsed : undefined;
    }
  }

  /**
   * Point d'entrée Angular.
   * - Ouvre la popup alcool si nécessaire.
   * - Lance l'initialisation des données (sale methods + booking + slots).
   */
  ngOnInit(): void {
    // Si le point de vente vend de l'alcool et que la popup ne s'est jamais ouverte alors on l'ouvre.
    if (this.currPoint && this.currPoint.has_alcool > 0 && !this.isAlreadyCheckedAlcool()) {
      this.openAlertAlcoolDial(this.currPoint);
    }
    this.initData();
  }

  /**
   * Initialise les données de l'écran :
   * - reset des clés liées à une commande/paiement
   * - récupération booking settings + sale methods
   * - enrichissement des sale methods avec slots (prochaine dispo)
   * - cas particuliers : auto-sélection si 1 seul SM, redirection booking, sélection depuis URL
   */
  initData(): void {
    this.loader = true;

    // Reset état commande/paiement (évite de traîner un ancien paiement/Order ID)
    this.tools.removeStor("order_id");
    this.tools.removeStor("ClientKeyPayIntentOrder");

    // Si on change de SM depuis l'URL, on évite de garder un ancien smId incohérent
    if (this.tools.getStor("smId") && this.currSmId !== Number(this.tools.getStor("smId"))) {
      this.tools.removeStor("smId");
    }

    forkJoin([
      this.trService.$_getBookingSettings(this.currPoint?.id_webshop),
      this.smApi.getSaleMethods(),
    ]).subscribe({
      next: async ([bookingSettings, saleMethods]: [any, SaleMethod[]]) => {
        // Booking actif => on garde la config
        if (bookingSettings?.code > 0) {
          this.bookingSettings = bookingSettings.object;
        }

        // Enrichit les SM avec notavailable + slotOpen
        const enrichedSaleMethods = await this.getSlots(saleMethods);

        // Flag ponctuel (stockage) : "fermé aujourd’hui", utilisé ailleurs dans le flow
        const closedForToday = this.tools.getStor("closedForToday") ?? null;
        this.tools.removeStor("closedForToday");

        // 1 seul SM + booking inactif => auto-sélection
        if (enrichedSaleMethods.length === 1 && bookingSettings?.code < 0 && !closedForToday) {
          this.handleOnlyOneSm(enrichedSaleMethods);
        }
        // Aucun SM mais booking actif => page booking
        else if (enrichedSaleMethods.length === 0 && bookingSettings?.code > 0) {
          this.goBooking();
        }
        // Ouverture depuis URL => auto-selection si possible
        else if (this.fromUrl && !closedForToday) {
          this.handleFromUrl(enrichedSaleMethods);
        }

        this.saleMethods$ = enrichedSaleMethods;
        this.loader = false;
      },
      error: (err) => {
        console.error("[FulleSalemethod] initData error", err);
        this.saleMethods$ = [];
        this.loader = false;
      },
    });

    // Ajoute les tailles du logo et de la bannière (enrichissement du point de vente)
    this.logoAndBannerService
      .initLogoAndBannerDetails(this.currPoint)
      .then((enrichedPoint: PointOfSaleDetails) => {
        if (enrichedPoint) {
          this.currPoint = enrichedPoint;
        }
      })
      .catch((err) => {
        console.warn("[FulleSalemethod] initLogoAndBannerDetails error", err);
      });
  }

  // Indique si la popup alcool a déjà été validée.
  isAlreadyCheckedAlcool(): boolean {
    return Boolean(this.alcoolCheck);
  }

  // Ouvre la popup d'avertissement alcool (si le PDV vend de l'alcool).
  openAlertAlcoolDial(pointOfSale: any): void {
    this.dialogsService.openAlertAlcoolDial(this.dialogRef, pointOfSale);
  }

  /**
   * Cas particulier : un seul mode de vente disponible.
   * - Si c'est déjà celui courant => set + close
   * - Sinon => si livraison: check address, sinon set + close
   */
  handleOnlyOneSm(saleMethods: SaleMethod[]): void {
    const sm = saleMethods[0];

    if (this.currSmId && sm.id === this.currSmId) {
      this.smApi.setSaleMeth(sm, true);
      this.dialogRef.close(sm); // on renvoie tout de meme la valeur de sm pour la suite dans Product.
      return;
    }

    if (sm.enable_delivery === true) {
      this.openCheckAddress(sm);
      return;
    }

    this.smApi.setSaleMeth(sm, null);
    this.dialogRef.close(sm);
  }

  /**
   * Cas particulier : ouverture depuis URL.
   * Si un currSmId est fourni, on tente de le sélectionner automatiquement.
   */
  handleFromUrl(saleMethods: SaleMethod[]): void {
    if (!this.currSmId) return;

    const target = saleMethods.find((sm) => sm.id === this.currSmId);
    if (!target) return;

    this.tools.setStor("smId", target.id);
    this.selectSM(target);
  }

  /**
   * Sélection d'un mode de vente (clic utilisateur).
   * Règles :
   * - si SM indispo => ne rien faire
   * - si livraison désactivée => reset countriesEligible
   * - si panier déjà existant => prévenir perte panier
   * - sinon : si livraison => check address, sinon set + close
   */
  selectSM(sm: SaleMethod): void {
    if (sm.notavailable) return;

    if (sm.enable_delivery === false) {
      this.tools.removeStor("countriesEligible");
    }

    if (this.currSmId && sm.id === this.currSmId) {
      this.smApi.setSaleMeth(sm, true);
      this.dialogRef.close(sm); // on renvoie tout de meme la valeur de sm pour la suite dans Product.
      return;
    }

    if (this.panierService.issetPanier()) {
      this.openPrevPan(sm);
      return;
    }

    if (sm.enable_delivery === true) {
      this.openCheckAddress(sm);
      return;
    }

    this.smApi.setSaleMeth(sm, null);
    this.dialogRef.close(sm);
  }

  /**
   * Ouvre le dialog "perte de panier" si on change de SM alors qu'une commande existe déjà.
   * Si l'utilisateur confirme => on continue la sélection.
   */
  openPrevPan(sm: SaleMethod): void {
    const myConf = new MatDialogConfig();
    myConf.autoFocus = false;
    this.media.isActive("xs") ? (myConf.width = "100%") : (myConf.width = "500px");
    myConf.data = { from: "sm" };

    const myDial = this.dialog.open(PreventLossPanierComponent, myConf);

    myDial.afterClosed().subscribe((result) => {
      if (result !== "continue") return;

      if (sm.enable_delivery === true) {
        this.openCheckAddress(sm);
      } else {
        this.smApi.setSaleMeth(sm, null);
        this.dialogRef.close(sm);
      }
    });
  }

  /**
   * Ouvre le dialog de vérification de code postal (livraison).
   * Si validation OK => set sale method + close.
   */
  openCheckAddress(sm: SaleMethod): void {
    const myConf = new MatDialogConfig();
    this.media.isActive("xs") ? (myConf.width = "100%") : (myConf.minWidth = "400px");
    myConf.autoFocus = true;
    myConf.data = { fromSmDial: true };

    const myDial = this.dialog.open(CheckAddressComponent, myConf);

    // Permet de gérer le bouton "back" (logique historique du projet)
    this.tools.setOnPopState(true, null, "openCheckAddress");

    myDial.afterClosed().subscribe((result) => {
      if (!result) return;

      this.smApi.setSaleMeth(sm, null);
      this.dialogRef.close(sm);
    });
  }

  /**
   * Redirige vers la page de réservation de table.
   * Utilisé quand il n'y a pas de modes de vente "classiques" disponibles mais que la réservation est active.
   */
  goBooking(): void {
    this.router.navigate([`/tr/${this.currPoint.url_encode}/${this.currPoint.id_webshop}`]);
    this.dialogRef.close();
  }

  /**
   * Ferme explicitement le dialog.
   */
  closeDial(): void {
    this.dialogRef.close();
  }

  /**
   * Enrichit les modes de vente avec :
   * - sm.notavailable : true si le SM est fermé (pas de slot dispo / code -82)
   * - sm.slotOpen : prochaine date/heure dispo (si enable_date_hour)
   *
   * Règle métier supplémentaire :
   * - si le "slot par défaut" (id=0) indique fermé => on force tous les SM à indispo
   *
   * @param saleMethods liste des modes de vente bruts (API)
   * @returns liste enrichie
   */
  async getSlots(saleMethods: SaleMethod[]): Promise<SaleMethod[]> {
    let isClosedByDefault = false;

    // 1) Vérifie l'état "par défaut"
    try {
      const defaultData = (await this.orderApi.getValidSlotsPof(0)) as ValidSlotsResponse;
      isClosedByDefault = defaultData?.code === -82;
    } catch (error) {
      console.warn(
        "[FulleSalemethod] Pas de méthode de vente par défaut (id=0) ou erreur lors de la récupération",
        error
      );
    }

    // 2) Enrichit chaque SM
    await Promise.all(
      saleMethods.map(async (sm) => {
        sm.notavailable = false;

        // ⚠️ Pour que ceci compile strict, ton interface SaleMethod doit accepter null :
        // slotOpen: {...} | null
        sm.slotOpen = null;

        // pas de gestion date/heure => pas de slot à afficher
        if (!sm.enable_date_hour) return;

        // fermé par défaut => on force indisponible (fallback cohérent)
        if (isClosedByDefault) {
          sm.notavailable = true;
          sm.slotOpen = {
            day: moment().format("YYYY-MM-DD"),
            hour: null,
            sameday: true,
          };
          return;
        }

        try {
          const data = (await this.orderApi.getValidSlotsPof(sm.id)) as ValidSlotsResponse;

          sm.notavailable = data?.code === -82;

          const day = data?.day_changed ?? moment().format("YYYY-MM-DD");
          const slots = data?.valid_slots ?? [];
          const firstAvailable = slots.find((v) => v.available);

          sm.slotOpen = {
            day,
            hour: firstAvailable ? firstAvailable.hour : null,
            sameday: moment().format("YYYY-MM-DD") === day,
          };
        } catch (error) {
          console.error(`[FulleSalemethod] Erreur slots pour méthode ${sm.id}`, error);

          // fallback : on laisse accessible mais sans slot
          sm.notavailable = false;
          sm.slotOpen = {
            day: moment().format("YYYY-MM-DD"),
            hour: null,
            sameday: true,
          };
        }
      })
    );

    return saleMethods;
  }
}